import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { describeError } from '../describeError';
import { formatDateRange } from '../format';
import { useValidationSession } from '../hooks/useValidationSession';
import { payrollHistoryImportGateway as gateway } from '../index';
import { batchStepPath, usesGuidedMapping } from '../routes';
import { saveDownloadedFile } from '../saveBlob';
import type { BatchDetail, Capabilities } from '../types';
import { ErrorSummary } from './basics';
import { IssueGroups } from './IssueGroups';

interface Props {
  batch: BatchDetail;
  capabilities: Capabilities;
  companyName: string;
  announce: (message: string) => void;
  refreshBatch: () => Promise<void>;
}

/** Step 7: everything validation found, sorted into errors (must fix), warnings (please review) and information,
 * each with a plain explanation, plus the downloadable validation report. */
export function ProblemsStep({ batch, capabilities, companyName, announce, refreshBatch }: Props) {
  const session = useValidationSession(batch.id, announce, refreshBatch);
  const { loading, data, error, reload, busy, actionError, setActionError, apply } = session;
  const guided = usesGuidedMapping(batch);

  const downloadReport = async () => {
    setActionError(null);
    try {
      const file = await gateway.downloadErrorReport(batch.id);
      saveDownloadedFile(file);
      announce(`Downloaded ${file.fileName}.`);
    } catch (err) {
      setActionError(describeError(err, 'Could not download the validation report.'));
    }
  };

  if (loading) return <p className="muted" role="status">Loading validation results…</p>;
  if (error !== undefined || !data) {
    return (
      <div>
        <ErrorSummary message={describeError(error, 'Could not load the validation results.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }

  const s = data;
  const ready = s.status === 'ReadyForReview';
  const validationFailed = s.status === 'ValidationFailed';
  const nextHintId = 'phi-next-hint';
  const employeeProblems = s.issues.some((i) => i.code === 'EMPLOYEE_MAPPING_MISSING' || i.code === 'DUPLICATE_EMPLOYEE_MAPPING');

  return (
    <div>
      {actionError && <ErrorSummary message={actionError} />}
      {ready && s.errorCount === 0 && (
        <p>
          Nothing blocks your import.{' '}
          {s.warningCount > 0 ? 'Review the warnings below, then continue.' : 'There are no warnings either. You can continue.'}
        </p>
      )}
      {validationFailed && (
        <p>
          This import cannot continue until every blocking error below is fixed. Each one says what it means and what to do.
        </p>
      )}

      <section aria-labelledby="phi-summary-heading">
        <h3 id="phi-summary-heading">Validation summary</h3>
        <dl className="phi-summary">
          <div><dt>Importing into company</dt><dd className="phi-summary-text">{companyName}</dd></div>
          <div><dt>Source payroll company</dt><dd className="phi-summary-text">{s.sourceCompanyName ?? s.sourceCompanyReference ?? '—'}</dd></div>
          <div><dt>Pay periods detected</dt><dd>{s.payRunsDetected}<span className="muted"> {formatDateRange(s.payPeriodStart, s.payPeriodEnd)}</span></dd></div>
          <div><dt>Employees detected</dt><dd>{s.employeesDetected}</dd></div>
          <div><dt>Employees matched</dt><dd>{s.employeesMatched}</dd></div>
          <div><dt>Employees requiring mapping</dt><dd>{s.employeesRequiringMapping}</dd></div>
          <div><dt>Earning codes</dt><dd className="phi-summary-text">{s.earningCodesDetected.join(', ') || '—'}</dd></div>
          <div><dt>Deduction codes</dt><dd className="phi-summary-text">{s.deductionCodesDetected.join(', ') || '—'}</dd></div>
          <div><dt>Tax jurisdictions</dt><dd className="phi-summary-text">{s.taxJurisdictionsDetected.join(', ') || '—'}</dd></div>
          <div><dt>Warnings</dt><dd>{s.warningCount}</dd></div>
          <div><dt>Blocking errors</dt><dd>{s.errorCount}</dd></div>
        </dl>
      </section>

      <IssueGroups issues={s.issues} truncated={s.issuesTruncated} explain />

      <div className="phi-actions">
        {capabilities.canCreate && (validationFailed || ready) && (
          <Button
            type="button"
            className="phi-btn-secondary"
            loading={busy === 'revalidate'}
            loadingLabel="Revalidating…"
            onClick={() => void apply('revalidate', () => gateway.revalidate(batch.id), (r) => `Revalidated. ${r.errorCount} blocking errors and ${r.warningCount} warnings.`)}
          >
            Revalidate
          </Button>
        )}
        {capabilities.canCreate && validationFailed && employeeProblems && (
          <Link to={batchStepPath(batch.id, 'employees')} className="phi-btn-secondary">Fix employee matches</Link>
        )}
        {capabilities.canCreate && guided && (validationFailed || ready) && (
          <Link to={batchStepPath(batch.id, 'map')} className="phi-btn-secondary">Change column mapping</Link>
        )}
        <button type="button" className="phi-link-button" onClick={() => void downloadReport()}>
          Download validation report
        </button>
      </div>

      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, ready ? 'totals' : 'employees')} className="phi-btn-secondary">Back</Link>
        <div className="phi-footer-group">
          {!ready && (
            <span id={nextHintId} className="muted">
              Fix every blocking error to continue.
            </span>
          )}
          {ready ? (
            <Link to={batchStepPath(batch.id, 'confirm')} className="phi-btn-primary">Next: Confirm</Link>
          ) : (
            <button type="button" disabled aria-describedby={nextHintId}>
              Next: Confirm
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
